// ============================================================
// TMR Portal Admin — Sincronização de taxas (rota /taxas/sincronizacao)
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { Notice } from '../../components/Notice';
import { PageHeader } from '../../components/ui';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import { useRatesOutOfSync, useReconcilePage } from '../../data/ratePortfolio/queries';
import {
  EMPTY_HINT,
  EMPTY_MESSAGE,
  accumulate,
  emptyReason,
  emptyRun,
  runSummary,
} from '../../data/ratePortfolio/status';
import type { ReconcileRun } from '../../data/ratePortfolio/contract';
import { CompanyDriftRow } from './CompanyDriftRow';

/**
 * The Empresas whose rates disagree with the legacy — the portfolio view of drift (`15` §3 E).
 *
 * <b>This is the only screen that asks the other system.</b> An Empresa's tab shows "aguardando sincronia"
 * by comparing the V2 with its own memory of what it exported, which sees nothing of a coupon rewriting
 * values in bulk, a row edited in the ScriptCase back-office, or an export abandoned after its retries. In
 * all three the row reads green there and the clinic is charged something else.
 *
 * Two things it must not get wrong, and both are about empty lists. **An empty list has three meanings** —
 * everything agrees, the legacy would not answer, or the operator reaches no Empresa — and showing the
 * reassuring one when it means either of the others is the failure mode this screen exists to prevent. And
 * **`awaitingExport` is not divergence**: it is our own outbox still working, so it is shown apart from the
 * counts that mean the legacy moved underneath us.
 */
export function RateSyncPage() {
  // Where the list is reading from. Kept as the cursor rather than a page number because that is what the
  // endpoint pages by — Empresas gain and lose rates between pages, and an offset would skip them.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [run, setRun] = useState<ReconcileRun | null>(null);
  const [sweeping, setSweeping] = useState(false);
  // Read inside the walk, so pressing "Parar" takes effect between pages rather than after the last one.
  const [stopRequested, setStopRequested] = useState(false);

  const canRead = usePermission(PERMISSIONS.companyRatesAdmin);
  const canManageCompanyRates = usePermission(PERMISSIONS.companyRatesManage);

  const query = useRatesOutOfSync(cursor, canRead);
  const reconcile = useReconcilePage();

  /**
   * Walks the portfolio a page at a time, carrying the cursor, until the base ends or the operator stops.
   *
   * The loop lives here rather than on the server because the decision to carry on is the operator's: each
   * page is a legacy round trip per Empresa, and a walk that could not be interrupted would be a button
   * that commits to an unknown number of minutes against somebody else's production database.
   */
  const sweep = async () => {
    setSweeping(true);
    setStopRequested(false);

    let total = emptyRun();
    let after: string | null = null;
    let stopped = false;

    try {
      for (;;) {
        const page = await reconcile.mutateAsync(after);
        total = accumulate(total, page);
        setRun(total);

        if (page.lastCompanyId === null) break;

        if (stopRequested) {
          stopped = true;
          break;
        }

        after = page.lastCompanyId;
      }
    } finally {
      setSweeping(false);
      if (stopped) setRun({ ...total, completed: false });
      // Back to the first page: the comparison the list is showing is about rates this walk just rewrote.
      setCursor(undefined);
      setExpanded(null);
    }
  };

  if (!canRead) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <div className="pa-card">
          <EmptyState
            message="Você não tem permissão para ver a sincronização de taxas."
            hint="Peça o acesso a quem administra os papéis."
          />
        </div>
      </div>
    );
  }

  const reason = query.data ? emptyReason(query.data) : 'not-empty';

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Plataforma"
        title="Sincronização de taxas"
        desc="As empresas cujas taxas não batem com o sistema legado — comparado lendo o legado de volta, não a nossa própria memória."
        actions={
          sweeping ? (
            <Btn variant="secondary" icon={Icons.x} onClick={() => setStopRequested(true)}>
              {stopRequested ? 'Parando…' : 'Parar'}
            </Btn>
          ) : (
            <Btn variant="primary" icon={Icons.swap} onClick={() => void sweep()}>
              Reconciliar carteira
            </Btn>
          )
        }
      />

      {/* A walk is many requests over minutes, so it says where it is rather than spinning silently. */}
      {sweeping && run && (
        <Notice tone="neutral">
          Reconciliando… {run.companies === 1 ? '1 empresa' : `${run.companies} empresas`} até agora
          {run.pages > 1 ? ` (${run.pages} páginas)` : ''}.
        </Notice>
      )}

      {!sweeping && run && <Notice tone={run.failed > 0 ? 'warning' : 'success'}>{runSummary(run)}</Notice>}

      {reconcile.error && (
        <Notice tone="danger">
          {reconcile.error instanceof ApiError
            ? reconcile.error.detail
            : 'Não foi possível reconciliar a carteira.'}
        </Notice>
      )}

      {query.isPending ? (
        <Skeleton rows={5} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : reason !== 'not-empty' ? (
        <div className="pa-card">
          <EmptyState message={EMPTY_MESSAGE[reason]} hint={EMPTY_HINT[reason]} />
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
            {query.data.items.length === 1
              ? '1 empresa fora de sincronia'
              : `${query.data.items.length} empresas fora de sincronia`}
            {' · '}
            {query.data.companiesChecked === 1
              ? '1 comparada nesta página'
              : `${query.data.companiesChecked} comparadas nesta página`}
          </div>

          <div className="pa-card" style={{ padding: 0, overflow: 'hidden' }}>
            {query.data.items.map((item) => (
              <CompanyDriftRow
                key={item.companyId}
                item={item}
                expanded={expanded === item.companyId}
                canReconcile={canManageCompanyRates}
                onToggle={() =>
                  setExpanded((current) => (current === item.companyId ? null : item.companyId))}
              />
            ))}
          </div>

          {/* Cursor paging, so "próxima página" is the only honest control: there is no total to build a
              page picker from, and the set moves while it is being walked. */}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <Btn
              variant="secondary"
              onClick={() => {
                setCursor(undefined);
                setExpanded(null);
              }}
              disabled={cursor === undefined || query.isFetching}
            >
              Voltar ao início
            </Btn>

            <Btn
              variant="secondary"
              icon={Icons.arrowR}
              onClick={() => {
                setCursor(query.data.lastCompanyId ?? undefined);
                setExpanded(null);
              }}
              disabled={query.data.lastCompanyId === null || query.isFetching}
            >
              {query.data.lastCompanyId === null ? 'Fim da carteira' : 'Próxima página'}
            </Btn>
          </div>
        </>
      )}
    </div>
  );
}
